import type { ReactNode } from "react";
import {
  DragHandle,
  dragChrome,
  type DragHandleSize,
} from "@/components/ui/DragHandle";
import {
  useDragItem,
  useDropTarget,
  useSortableRow,
} from "@/components/ui/SortableLanes";

/**
 * The pieces a list reordered by drag is drawn from, so a screen asks for a
 * row, a carried item or a target and gets the machinery, the named handle and
 * the drag look together. Used inside `SortableLanes`.
 */
interface Handled {
  ref: (element: Element | null) => void;
  handle: ReactNode;
  /** The row's look during a drag: faded while carried. */
  chrome: string;
}

/** A row that sorts among its siblings in one lane, moved by its handle. */
export function useSortableItem({
  id,
  index,
  lane,
  type,
  label,
  size = "md",
}: {
  id: string;
  index: number;
  lane: string;
  type: string;
  label: string;
  size?: DragHandleSize;
}): Handled {
  const { ref, handleRef, isDragging } = useSortableRow({
    id,
    index,
    lane,
    type,
    accept: type,
  });
  return {
    ref,
    handle: <DragHandle size={size} label={label} handleRef={handleRef} />,
    chrome: dragChrome({ dragging: isDragging }),
  };
}

/** An item carried onto a target rather than sorted, moved by its handle. */
export function useCarriedItem({
  id,
  type,
  label,
  size = "md",
}: {
  id: string;
  type: string;
  label: string;
  size?: DragHandleSize;
}): Handled {
  const { ref, handleRef, isDragging } = useDragItem({ id, type });
  return {
    ref,
    handle: <DragHandle size={size} label={label} handleRef={handleRef} />,
    chrome: dragChrome({ dragging: isDragging }),
  };
}

/** Somewhere a carried item lands, outlined while a release would land on it. */
export function useTarget({
  id,
  accept,
  inner,
  depth,
}: {
  id: string;
  accept: string | string[];
  /** A target inside another target, which wins when the pointer is over both. */
  inner?: boolean;
  /** How deep it sits inside other targets, so the one the pointer is really over wins. */
  depth?: number;
}) {
  const { ref, isDropTarget } = useDropTarget({ id, accept, inner, depth });
  return { ref, chrome: dragChrome({ landing: isDropTarget }) };
}
